// drawer-component.js
class DrawerComponent extends HTMLElement {
  constructor() {
    super();

    // this.attachShadow({ mode: 'open' });

    this._isOpen = false;
    this._position = 'right';
    this._focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  }

  connectedCallback() {
    this._position = this.getAttribute('position') || 'right';
    this._width = this.getAttribute('width');
    this._setupDrawer();
    this._addEventListeners();
    this.isOpen = this.hasAttribute('open');

    if (this.shadowRoot) {
      this._loadStyles();
    }
  }

  async _loadStyles() {
    try {
      const styleURL = this.dataset.style;
      const response = await fetch(styleURL);
      const styles = await response.text();
      const styleSheet = new CSSStyleSheet();
      styleSheet.replaceSync(styles);
      this.shadowRoot.adoptedStyleSheets = [styleSheet];
    } catch (error) {
      console.error('Impossibile caricare gli stili:', error);
    }
  }

  _setupDrawer() {
    // Salva il contenuto originale
    const currentContent = this.innerHTML;

    const content = `
      <div class="drawer-overlay" data-drawer-overlay></div>
      <div class="drawer" 
           data-drawer
           role="dialog"
           aria-modal="true"
           aria-hidden="true"
           aria-labelledby="drawer-title">
        <header class="drawer-header">
          <div id="drawer-title">
            <slot name="title">${this.getAttribute('title')}</slot>
          </div>
          <button class="drawer-close"
                  data-drawer-close
                  aria-label="Chiudi">&times;</button>
        </header>
        <main class="drawer-content">
          ${currentContent}
        </main>
      </div>
    `;

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = content;
    } else {
      this.innerHTML = content;
    }

    this._drawer = this.shadowRoot ?
      this.shadowRoot.querySelector('[data-drawer]') : this.querySelector('[data-drawer]');
    this._overlay = this.shadowRoot ?
      this.shadowRoot.querySelector('[data-drawer-overlay]') : this.querySelector('[data-drawer-overlay]');
    this._closeButton = this.shadowRoot ?
      this.shadowRoot.querySelector('[data-drawer-close]') : this.querySelector('[data-drawer-close]');

    if (this._width) {
      this.style.setProperty('--drawer-width', this._width);
    }

    this._updateDrawerPosition();
  }

  _addEventListeners() {
    this._closeButton?.addEventListener('click', () => this.close());
    this._overlay?.addEventListener('click', () => this.close());
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleClickOutside = this._handleClickOutside.bind(this);
    document.addEventListener('keydown', this._handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._handleKeydown);
  }

  _handleKeydown(event) {
    if (!this._isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this._handleTabKey(event);
    }
  }

  _handleTabKey(event) {
    const focusableElements = Array.from(this._drawer.querySelectorAll(this._focusableElements));
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  _handleClickOutside() {
    this.close();
  }

  _updateDrawerState() {
    if (this._isOpen) {
      this._drawer.classList.add('open');
      this._overlay.classList.add('open');
      this._drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden'; // html

      this._previousActiveElement = document.activeElement;
      this._focusFirstElement();
    } else {
      this._drawer.classList.remove('open');
      this._overlay.classList.remove('open');
      this._drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';

      if (this._previousActiveElement) {
        this._previousActiveElement.focus();
      }
    }
  }

  _updateDrawerPosition() {
    if (this._drawer) {
      this._drawer.setAttribute('position', this._position);
    }
  }

  _focusFirstElement() {
    setTimeout(() => {
      const firstFocusable = this._drawer.querySelector(this._focusableElements);
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);
  }

  // API Pubblica
  open() {
    this._isOpen = true;
    this._updateDrawerState();
  }

  close() {
    this._isOpen = false;
    this._updateDrawerState();
  }

  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

customElements.define('drawer-component', DrawerComponent);
